<template>
  <b-card class="card-box card__user-submission w-100">
    <b-row>
      <b-col cols="12" md="4" class="mb-3">
        <div class="d-flex justify-content-between">
          <div v-if="!hideSubmissionId">
            <h6 class="font-weight-bold">Submission ID</h6>
            <div ref="text__code" class="text__code mb-3">
              {{ selectedEvidenceSubmission.code }}
            </div>
          </div>
        </div>

        <base-select
          ref="select__submissions"
          class="select__submissions"
          label="Submitted Date"
          label-bold
          :default-value="$_selectedEvidenceSubmissionId"
          :options-select="selectOptions"
          value-field="value"
          text-field="text"
          @onFilterSelected="onSelectChanged"
        ></base-select>

        <h6 class="font-weight-bold">Status</h6>

        <div>
          <b-badge
            ref="badge__submission-status"
            pill
            :variant="badgeStatusVariant"
            :class="badgeStatusClass"
          >
            <span class="text__status">{{ readableStatus }}</span>
          </b-badge>
        </div>
      </b-col>

      <b-col cols="12" md="8" class="mb-3">
        <AssessorManualDetail v-if="isShowAssessorManual" :learning-unit-id="learningUnit.id" />
      </b-col>
    </b-row>

    <div
      v-if="
        canCreateNewSubmission &&
        isLearner &&
        isApplied &&
        (isApplied.remaining_submission == 0 || !isApplied.remaining_submission)
      "
    >
      <hr />

      <h6 class="font-weight-bold">What would you do next?</h6>

      <card-remaining-evidence-submission
        :learning-unit="learningUnit"
      ></card-remaining-evidence-submission>
    </div>
    <redeem-code-modal
      :show.sync="activeModalRedeem"
      :mc-id="learningUnit.id"
      :redeem-type="'QUOTA'"
      :success-alert-message="successAlertMessage"
      @redeem-success:hide="onCloseModalRedeem"
    ></redeem-code-modal>
  </b-card>
</template>

<script>
import { orderBy } from 'lodash';
import User from '@/models/User';
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import BaseSelect from '../base-components/BaseSelect';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';
import CardRemainingEvidenceSubmission from './CardRemainingEvidenceSubmission.vue';
import Application from '@/models/Application';
import RedeemCodeModal from '@/components/LearningUnit/RedeemCodeModal.vue';
import AssessorManualDetail from '@/views/EvidenceSubmission/components/AssessorManualDetail.vue';
const { formatedDatetime } = useDateTimeFormat();

export default {
  components: {
    BaseSelect,
    CardRemainingEvidenceSubmission,
    RedeemCodeModal,
    AssessorManualDetail
  },

  model: {
    prop: 'selectedEvidenceSubmissionId',
    event: 'change'
  },
  props: {
    user: {
      type: User,
      required: true
    },

    learningUnit: {
      type: LearningUnit,
      required: true
    },

    selectedEvidenceSubmissionId: {
      type: [Number, String],
      required: true
    },

    view: {
      type: String,
      default: ''
    },

    isShowAssessorManual: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeModalRedeem: false,
      applyFetching: false,
      successAlertMessage: 'Code redeemed successfully.'
    };
  },

  computed: {
    $_selectedEvidenceSubmissionId: {
      get() {
        return this.selectedEvidenceSubmissionId;
      },

      set(value) {
        this.$emit('change', value);
      }
    },

    isApplied() {
      return Application.query()
        .where((apply) => {
          return apply.user.id == this.user.id && apply.learning_unit.id == this.learningUnit.id;
        })
        .first();
    },

    evidenceSubmissions() {
      const evidenceSubmissions = EvidenceSubmission.query()
        .where('learning_unit_id', this.learningUnit.id)
        .where('user_id', this.user.id)
        .get();

      return orderBy(evidenceSubmissions, ['created_at'], 'desc');
    },

    selectOptions() {
      return this.evidenceSubmissions
        // Exclude project-ended submissions (denied with no assessor review)
        // from the dropdown options.
        .filter((submission) => !submission.isProjectEnded)
        .map(({ id, submitted_at }) => {
          return {
            value: id,
            text: submitted_at
              ? formatedDatetime(submitted_at, 'MMM D, YYYY hh:mm a ')
              : 'Latest Submission'
          };
        });
    },

    selectedEvidenceSubmission() {
      return EvidenceSubmission.find(this.$_selectedEvidenceSubmissionId);
    },

    canCreateNewSubmission() {
      return this.evidenceSubmissions.every((es) => es.isDenied);
    },

    hideSubmissionId() {
      return this.evidenceSubmissions.length === 1 && this.evidenceSubmissions[0].isNotSubmitted;
    },

    isLearner() {
      // TODO: change condition to use something like enrollment instead
      return (
        !this.$currentUser.isRole('issuer') &&
        !this.$currentUser.isLearningUnitMemberOf(this.learningUnit.id)
      );
    },

    evidenceSubmissionStatus() {
      return this.selectedEvidenceSubmission.status;
    },

    badgeStatusVariant() {
      let variant;

      switch (this.evidenceSubmissionStatus) {
        case 'REVIEW_PENDING':
          variant = 'neutral-info';
          break;

        case 'DENIED':
          variant = 'neutral-danger';
          break;

        case 'AWARDED':
          variant = 'neutral-success';
          break;

        default:
          variant = 'light';
          break;
      }

      return variant;
    },

    badgeStatusClass() {
      return {
        'badge__evidence-submission-not-submitted-status text-dark':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.NotSubmitted,
        'badge__evidence-submission-review-pending-status text-info':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.ReviewPending,
        'badge__evidence-submission-denied-status text-danger':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.Denied,
        'badge__evidence-submission-awarded-status text-success':
          this.evidenceSubmissionStatus === EvidenceSubmission.EnumStatus.Awarded
      };
    },

    readableStatus() {
      let status = this.selectedEvidenceSubmission.readableStatus;
      if (
        this.view === 'learner' &&
        (status === 'In Progress by Assessor' || status === 'In Progress by Issuer')
      ) {
        status = EvidenceSubmission.STATUS.REVIEW_PENDING;
      }

      if (this.view === 'learner' && status === 'Awarded') {
        status = EvidenceSubmission.STATUS.ACHIEVED;
      }
      return status;
    }
  },

  methods: {
    onSelectChanged(selectedEvidenceSubmissionId) {
      this.$_selectedEvidenceSubmissionId = selectedEvidenceSubmissionId;
    },

    async fetchApply() {
      if (!this.user.id) return;

      try {
        this.applyFetching = true;
        await Application.api().find({
          user: this.user.id,
          learning_unit: this.learningUnit.id
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.applyFetching = false;
      }
    },

    onClickRedeemCode() {
      this.activeModalRedeem = true;
    },

    onCloseModalRedeem() {
      this.activeModalRedeem = false;
      this.fetchApply();
    }
  }
};
</script>

<style scoped lang="scss">
.card__user-submission {
  max-width: 910px;
  margin: auto;

  .btn.button__redeem-code {
    padding: 0;
    margin: 0;
    text-align: start;
  }

  .btn-link.btn.button__redeem-code {
    text-decoration: underline;
  }

  .btn-link.btn.button__redeem-code:hover {
    text-decoration: none;
  }

  .select__submissions {
    width: 225px; // fix width
  }
}
</style>
